import React, { useState, useEffect, useRef } from 'react';
import ZoomInIcon from '../../assets/images/ZoomIn.svg';
import ZoomOutIcon from '../../assets/images/ZoomOut.svg';
import InteractiveWhiteBoardIcon from '../../assets/images/IWBIcon.svg';
import ViewIcon from '../../assets/images/viewIcon.svg';
import DoublePageViewIcon from '../../assets/images/DoublePageViewIcon.svg';
import SinglePageViewIcon from '../../assets/images/SinglePageIcon.svg';
import BackToNormalIcon from '../../assets/images/BackToNormal.svg';
import {
    backToNormal,
    handlePageChange,
    zoomIn,
    zoomOut,
} from '../../utils/readiumUtils';
import { trackEvent } from '../../segment';

interface Item {
    icon_path: string;
    description: string;
    handleClick: (window: Window) => void;
}

const ViewMenu: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSinglePageView, setIsSinglePageView] = useState(true);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const togglePageView = (window: Window) => {
        setIsSinglePageView((prev) => !prev);
        handlePageChange(window);
    };
    const changePageView = () => {
        togglePageView(window);
        const reader = window[0]?.READIUM.reader;
        if (reader) {
            const currentSettings = reader.viewerSettings();
            if (currentSettings.syntheticSpread === 'single') {
                currentSettings.syntheticSpread = 'auto';
                trackEvent('click_book_double_page_view', {});
            } else {
                //old event
                trackEvent('epub_viewer_view_single_page_view_clicked', {});
                trackEvent('click_book_single_page_view', {});
                currentSettings.syntheticSpread = 'single';
            }
            reader.updateSettings(currentSettings);
            handlePageChange(window);
        }
    };

    const items: Item[] = [
        {
            icon_path: isSinglePageView
                ? SinglePageViewIcon
                : DoublePageViewIcon,
            description: isSinglePageView
                ? 'Single page view'
                : 'Double page view',
            handleClick: changePageView,
        },
        {
            icon_path: ZoomInIcon,
            description: 'Zoom In',
            handleClick: zoomIn,
        },
        {
            icon_path: ZoomOutIcon,
            description: 'Zoom Out',
            handleClick: zoomOut,
        },
        {
            icon_path: BackToNormalIcon,
            description: 'Back to normal view',
            handleClick: backToNormal,
        },
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div
            ref={dropdownRef}
            className="z-30 hover:cursor-pointer h-12 w-12 flex flex-row-reverse items-end transition duration-500 ease-linear"
            onClick={() => setIsOpen(!isOpen)}
        >
            <div className="flex flex-col bg-white rounded-md min-w-[3.125rem] h-[3.125rem] items-center justify-center -space-y-1">
                <img
                    src={ViewIcon}
                    alt="view"
                    className="h-[1.6rem] w-[1.6rem]"
                />
                <p className="text-[#7B86A0] font-semibold text-xs">View</p>
            </div>
            {isOpen && (
                <div className="flex flex-col rounded-xl bg-white h-auto w-auto px-[0.625rem] py-[1rem] shadow-lg mr-1 justify-center">
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className="flex h-[2.8rem] w-[13.063rem] items-center justify-start hover:bg-[#EDF1F9] py-[0.813rem] transition-all duration-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                item.handleClick(window);
                            }}
                        >
                            <img
                                src={item.icon_path}
                                alt={item.description}
                                className="h-[1.5rem] w-[1.5rem] ml-5"
                            />
                            <p className="text-nowrap ml-[0.75rem] mr-5 text-xs font-sans">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ViewMenu;
